import { useEffect, useMemo, useRef, useState } from 'react';

import { ApiOrg, ApiSetSupplierAccess, ApiSupplierAccessRecord, ApiWarehouseSupplier, getSupplierCatalogFeatureFlag } from '@kaiizen/kai-pro-api';
import { useAllOrgs, useFeatureFlags, useSupplierAccess } from '@kaiizen/kai-pro-api-react-state';

import { useConfirmationModal, useToast } from '@/Client/State';
import { LabeledSelect } from '@/Client/Widgets';
import { CommonActionButton, CommonToggle } from '@kaiizen/kai-shiki/react';
import { OrgSettingChip } from './OrgSettingChip';
import { OrgSettingDialog } from './OrgSettingDialog';
import {
  buildOrgSettingDialog,
  describeDefault,
  describeOrgSetting,
  describeSupplierMeta,
  getDefaultRecord,
  getOrgRecords,
} from './superAdminSupplierAccessLib';
import { DEFAULT_SUPER_ADMIN_SUPPLIER_ACCESS_MANAGER_STYLES, SuperAdminSupplierAccessManagerStyles } from './superAdminSupplierAccessManagerStyles';

interface AccessToggleProps {
  checked: boolean;
  label: string;
  onClick: () => void;
  styles: SuperAdminSupplierAccessManagerStyles;
}

const AccessToggle = ({ checked, label, onClick, styles }: AccessToggleProps) => {
  const state = checked ? 'on' : 'off';
  return (
    <CommonToggle
      checked={checked}
      trackCss={styles.toggleTrackByState[state]}
      knobCss={styles.toggleKnobByState[state]}
      aria-label={label}
      onClick={onClick}
    />
  );
};

interface SupplierPanelProps {
  supplier: ApiWarehouseSupplier;
  records: ApiSupplierAccessRecord[];
  allOrgs: ApiOrg[];
  save: (payload: ApiSetSupplierAccess) => Promise<void>;
  remove: (supplierAccessId: string) => Promise<void>;
  styles: SuperAdminSupplierAccessManagerStyles;
}

const SupplierPanel = ({ supplier, records, allOrgs, save, remove, styles }: SupplierPanelProps) => {
  const { tryWithErrorToast } = useToast();
  const confirm = useConfirmationModal();
  const [newOrgId, setNewOrgId] = useState('');

  const defaultRecord = getDefaultRecord(records, supplier.id);
  const orgRecords = useMemo(() => getOrgRecords(records, supplier.id), [records, supplier.id]);
  const orgName = (orgId?: string) => allOrgs.find(org => org.id === orgId)?.name ?? orgId ?? '';
  // Every org stays pickable, so an org with a setting can be changed from here as well as from its row.
  const orgItems = useMemo(
    () =>
      allOrgs
        .map(org => {
          const row = orgRecords.find(candidate => candidate.orgId === org.id);
          return { value: org.id, label: row ? `${org.name} (${describeOrgSetting(row).toLowerCase()})` : org.name };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allOrgs, orgRecords],
  );

  /** Runs a write, showing a toast on failure; resolves true only when it succeeded. */
  const run = async (description: string, action: () => Promise<void>) =>
    (await tryWithErrorToast(description, async () => {
      await action();
      return true;
    })) === true;
  const saveWith = (payload: ApiSetSupplierAccess, description: string) => run(description, () => save(payload));

  // No confirmation: an org setting is one click to change back. The ref blocks a second click before the re-render disables the buttons.
  const pickerBusyRef = useRef(false);
  const [pickerBusy, setPickerBusy] = useState(false);
  const setOrgFromPicker = async (enabled: boolean) => {
    if (pickerBusyRef.current) return;
    pickerBusyRef.current = true;
    setPickerBusy(true);
    try {
      if (await saveWith({ supplierId: supplier.id, scope: 'org', orgId: newOrgId, enabled }, `set ${supplier.name} for ${orgName(newOrgId)}`)) setNewOrgId('');
    } finally {
      pickerBusyRef.current = false;
      setPickerBusy(false);
    }
  };

  // The dialog follows the org's current row, so a refetch while it is open updates it, and a removed row closes it.
  const [openOrgId, setOpenOrgId] = useState<string>();
  const openOrgRecord = orgRecords.find(row => row.orgId === openOrgId);
  useEffect(() => {
    if (openOrgId && !openOrgRecord) setOpenOrgId(undefined);
  }, [openOrgId, openOrgRecord]);
  const [orgDialogBusy, setOrgDialogBusy] = useState(false);
  const runFromOrgDialog = async (description: string, action: () => Promise<void>) => {
    setOrgDialogBusy(true);
    try {
      if (await run(description, action)) setOpenOrgId(undefined);
    } finally {
      setOrgDialogBusy(false);
    }
  };

  return (
    <div css={styles.supplierPanel}>
      <div css={styles.supplierHeader}>
        <span>
          {supplier.name} <span css={styles.supplierMeta}>{describeSupplierMeta(supplier)}</span>
        </span>
        <span css={styles.controls}>
          <span css={styles.supplierMeta}>{`Default: ${describeDefault(defaultRecord)}`}</span>
          <AccessToggle
            checked={defaultRecord?.enabled ?? false}
            label={`Default access for ${supplier.name}`}
            styles={styles}
            onClick={() =>
              confirm({
                title: `${defaultRecord?.enabled ? 'Turn off' : 'Turn on'} ${supplier.name} for every org?`,
                message: 'Orgs with their own setting keep it.',
                confirmLabel: defaultRecord?.enabled ? 'Turn off' : 'Turn on',
                onConfirm: () => saveWith({ supplierId: supplier.id, scope: 'default', enabled: !defaultRecord?.enabled }, `set the ${supplier.name} default`),
              })}
          />
        </span>
      </div>
      <div css={styles.supplierBody}>
        {orgRecords.length > 0 && <div css={styles.orgSettingsHeading}>{'Org settings'}</div>}
        {orgRecords.length > 0 && (
          <div css={styles.orgChips}>
            {orgRecords.map(row => (
              <OrgSettingChip
                key={row.id}
                orgName={orgName(row.orgId)}
                enabled={row.enabled}
                onClick={() => setOpenOrgId(row.orgId)}
                styles={styles.orgChip}
              />
            ))}
          </div>
        )}
        <div css={styles.addOrgRow}>
          <LabeledSelect label='Set for an org' value={newOrgId} items={orgItems} onUpdate={setNewOrgId} placeholder='Select org...' />
          <CommonActionButton disabled={!newOrgId || pickerBusy} onClick={() => setOrgFromPicker(true)}>{'Enable'}</CommonActionButton>
          <CommonActionButton disabled={!newOrgId || pickerBusy} onClick={() => setOrgFromPicker(false)}>{'Disable'}</CommonActionButton>
        </div>
      </div>
      {openOrgRecord && (
        <OrgSettingDialog
          {...buildOrgSettingDialog(supplier.name, orgName(openOrgRecord.orgId), openOrgRecord, defaultRecord)}
          busy={orgDialogBusy}
          styles={styles.orgDialog}
          onCancel={() => setOpenOrgId(undefined)}
          onReset={() => runFromOrgDialog(`reset the ${orgName(openOrgRecord.orgId)} setting`, () => remove(openOrgRecord.id))}
          onToggle={() =>
            runFromOrgDialog(
              `set ${supplier.name} for ${orgName(openOrgRecord.orgId)}`,
              () => save({ supplierId: supplier.id, scope: 'org', orgId: openOrgRecord.orgId!, enabled: !openOrgRecord.enabled }),
            )}
        />
      )}
    </div>
  );
};

interface SuperAdminSupplierAccessManagerProps {
  styles?: SuperAdminSupplierAccessManagerStyles;
}

const getStatusMessage = (
  { isLoading, error, shownCount, warehouseCount }: { isLoading: boolean; error?: Error; shownCount: number; warehouseCount: number },
) => {
  if (shownCount > 0) return undefined;
  if (isLoading) return 'Loading suppliers...';
  if (error) return `Could not load suppliers: ${error.message}`;
  if (warehouseCount > 0) return 'Every supplier is hidden until its catalog feature flag is on for you.';
  return 'No suppliers found in the warehouse.';
};

export const SuperAdminSupplierAccessManager = ({ styles = DEFAULT_SUPER_ADMIN_SUPPLIER_ACCESS_MANAGER_STYLES }: SuperAdminSupplierAccessManagerProps) => {
  const { suppliers: allSuppliers, records, save, remove, isLoading, error } = useSupplierAccess();
  const { featureFlags } = useFeatureFlags();
  // A supplier still rolling out stays hidden until its own catalog flag is on.
  const suppliers = allSuppliers.filter(supplier => {
    const flagName = getSupplierCatalogFeatureFlag(supplier.slug);
    return !flagName || featureFlags[flagName];
  });
  const { allOrgs } = useAllOrgs();
  const statusMessage = getStatusMessage({ isLoading, error, shownCount: suppliers.length, warehouseCount: allSuppliers.length });

  return (
    <div css={styles.root}>
      {statusMessage
        ? <div css={styles.status}>{statusMessage}</div>
        : suppliers.map(supplier => (
          <SupplierPanel key={supplier.id} supplier={supplier} records={records} allOrgs={allOrgs} save={save} remove={remove} styles={styles} />
        ))}
    </div>
  );
};
